import { Link } from "react-router-dom";
import { ShieldCheck, UserCheck, FileSearch, Lock, History, Ban } from "lucide-react";
import PageHero from "@/components/landing/PageHero";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/hooks/useSEO";
import { PRODUCT } from "@/config/brand";

const principles = [
  { icon: UserCheck, title: "People decide", body: "Every proposal is reviewed by a qualified user. Approval of a GxP record or decision always sits with an accountable person." },
  { icon: FileSearch, title: "Cited by design", body: "Proposals reference the permitted source documents they draw on, so reviewers can verify them before accepting anything." },
  { icon: History, title: "Audit-trailed", body: "Prompts, proposals, edits and acceptance are recorded against the user and record, giving inspectors a clear history." },
  { icon: Lock, title: "Inside the Spine boundary", body: "Assistance runs within your organization's workspace and role permissions. Your data is never used to train AI models." },
];

const useCases = [
  ["Document drafting", "Prepare first drafts of SOPs, investigation reports and summaries from approved source material for author and reviewer refinement."],
  ["Deviation triage", "Organize evidence, suggest classification and propose investigation questions so quality teams start from a structured view."],
  ["CAPA assistance", "Outline corrective and preventive options and effectiveness-check considerations for the CAPA owner to assess."],
  ["Audit readiness", "Assemble evidence packages and highlight documentation gaps while keeping links to the underlying controlled records."],
  ["Change management", "Summarize impact across documents, assets and laboratory methods to support a complete change assessment."],
  ["Supplier and contract review", "Compare quality agreements and supplier files against your requirements and flag clauses for reviewer attention."],
];

const controls = [
  ["Who can use assistance", "Access is granted per organization and per user by your administrators, aligned with existing roles."],
  ["What assistance can do", "Read permitted context, propose text, suggest classifications and assemble evidence for review."],
  ["What assistance cannot do", "Approve, sign, release, close or change the status of any controlled record, or act outside a user's permissions."],
  ["How oversight is evidenced", "Accepted and rejected proposals remain traceable in the audit trail, supporting periodic review of AI use."],
];

const faqs = [
  ["Does AI make GxP decisions in Spine?", "No. AI proposes and cites; qualified people review, edit and approve. Electronic signatures remain with accountable users."],
  ["Is our data used to train models?", "No. Customer content is processed only to provide assistance to your organization and is never used for model training."],
  ["How does this fit our validation approach?", "AI assistance is treated as a supporting function within your intended use. Your validation package describes its controls so your quality team can assess it under its risk-based approach."],
];

export default function AIGovernance() {
  useSEO({
    title: "AI Governance in Pharmalify Spine | Human-Approved GxP Assistance",
    description: "How AI assistance works inside Pharmalify Spine: cited proposals, role permissions, audit trails and human approval for every GxP decision.",
    canonical: "https://www.pharmalify.com/ai-agents",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    },
  });
  return (
    <main className="min-h-screen bg-background">
      <PageHero
        title="AI governance built into Spine"
        description="Assistance that speeds up quality work without moving accountability. AI proposes and cites; your qualified people decide."
      />

      <section className="container py-16" aria-labelledby="principles">
        <h2 id="principles" className="text-3xl font-bold text-foreground mb-8">Four governance principles</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {principles.map(({ icon: Icon, title, body }) => (
            <article key={title} className="rounded-xl border border-border bg-card p-6">
              <Icon className="h-6 w-6 text-accent mb-3" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-card-foreground mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-muted/30 py-16" aria-labelledby="use-cases">
        <div className="container">
          <h2 id="use-cases" className="text-3xl font-bold text-foreground mb-3">Where teams use assistance</h2>
          <p className="text-muted-foreground max-w-3xl mb-8">Each assistant works inside the related Spine workflow, with access granted by your administrators.</p>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map(([title, body]) => (
              <article key={title} className="rounded-xl border border-border bg-card p-6">
                <h3 className="font-semibold text-card-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="container py-16" aria-labelledby="controls">
        <h2 id="controls" className="text-3xl font-bold text-foreground mb-8">The control model</h2>
        <div className="divide-y divide-border rounded-xl border border-border bg-card">
          {controls.map(([title, body], i) => (
            <div key={title} className="grid gap-2 p-6 md:grid-cols-[260px_1fr]">
              <h3 className="font-semibold text-card-foreground inline-flex items-center gap-2">
                {i === 2 ? <Ban className="h-4 w-4 text-destructive" aria-hidden="true" /> : <ShieldCheck className="h-4 w-4 text-accent" aria-hidden="true" />}
                {title}
              </h3>
              <p className="text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/30 py-16" aria-labelledby="ai-faq">
        <div className="container max-w-3xl">
          <h2 id="ai-faq" className="text-3xl font-bold text-foreground mb-8">Questions from quality leaders</h2>
          <div className="space-y-6">
            {faqs.map(([q, a]) => (
              <div key={q}>
                <h3 className="font-semibold text-foreground mb-1">{q}</h3>
                <p className="text-muted-foreground">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container max-w-3xl py-16 text-center">
        <h2 className="text-3xl font-bold text-foreground mb-4">Review the controls with your team</h2>
        <p className="text-muted-foreground mb-8">We walk your quality and IT leads through permissions, audit trail and validation evidence for AI assistance.</p>
        <Button asChild size="lg"><Link to="/contact">{PRODUCT.cta.primary}</Link></Button>
        <Link to="/spine" className="inline-block ml-6 text-accent underline">Explore Pharmalify Spine</Link>
      </section>
    </main>
  );
}
